import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Flame, Check } from "lucide-react";
import { toast } from "sonner";
import api, { errText } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CoinIcon } from "@/components/Icons";

export default function DailyRewards() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () => { setError(false); api.get("/rewards/daily").then(({ data: d }) => setData(d)).catch(() => setError(true)); };
  useEffect(() => { load(); }, []);

  const claim = async () => {
    setBusy(true);
    try {
      const { data: res } = await api.post("/rewards/claim");
      toast.success(`🎉 +${res.amount} coins claimed! ${res.streak}-day streak`);
      setUser((u) => u && { ...u, coins: res.coins });
      load();
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setBusy(false);
    }
  };

  const claimWatch = async () => {
    try {
      const { data: res } = await api.post("/rewards/claim-watch");
      toast.success("🎁 +25 bonus coins!");
      setUser((u) => u && { ...u, coins: res.coins });
      load();
    } catch (e) {
      toast.error(errText(e));
    }
  };

  if (error && !data) return (
    <div className="min-h-screen max-w-md mx-auto flex flex-col items-center justify-center gap-3" data-testid="rewards-error">
      <p className="text-zinc-400 text-sm">Couldn't load rewards</p>
      <button data-testid="rewards-retry" onClick={load} className="gradient-primary text-white text-xs font-bold px-5 py-2 rounded-full">Retry</button>
    </div>
  );
  if (!data) return <div className="min-h-screen max-w-md mx-auto flex items-center justify-center"><div className="w-8 h-8 rounded-full border-2 border-[#E6B84C] border-t-transparent animate-spin" /></div>;

  const doneCount = data.claimed_today ? ((data.streak - 1) % 7) + 1 : (data.streak % 7);

  return (
    <div className="min-h-screen max-w-md mx-auto px-4 py-6 pb-10 relative overflow-hidden" data-testid="rewards-page">
      <div className="absolute -top-20 left-1/2 -translate-x-1/2 w-80 h-40 rounded-full bg-[#E6B84C]/15 blur-3xl pointer-events-none" />
      <div className="flex items-center justify-between mb-2">
        <button data-testid="rewards-back" onClick={() => navigate(-1)} className="text-[#E6B84C]"><ArrowLeft size={20} /></button>
        <h1 className="font-royal text-2xl gradient-gold-text font-bold tracking-wide">DAILY REWARDS</h1>
        <Flame size={19} className="text-orange-400" />
      </div>
      <p className="text-center text-orange-300 text-xs font-bold mb-6" data-testid="streak-label">🔥 {data.streak}-day streak</p>

      <div className="grid grid-cols-4 gap-2 mb-2" data-testid="reward-days">
        {data.schedule.map((amt, i) => {
          const claimed = i < doneCount;
          const isToday = !data.claimed_today && i === data.today_index;
          return (
            <motion.div key={i} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
              data-testid={`reward-day-${i + 1}`}
              className={`relative rounded-2xl p-2.5 text-center border ${isToday ? "border-[#E6B84C] shadow-[0_0_16px_rgba(230,184,76,0.4)] bg-[#E6B84C]/10" : claimed ? "border-emerald-500/40 bg-emerald-500/5" : "border-white/10 bg-[#131A2A]"} ${i === 6 ? "col-span-1" : ""}`}>
              {isToday && <span className="absolute -top-2 left-1/2 -translate-x-1/2 bg-[#E6B84C] text-[#0B0F19] text-[8px] font-extrabold px-2 py-0.5 rounded-full">TODAY</span>}
              <p className="text-[9px] text-zinc-500 font-bold">Day {i + 1}</p>
              <p className="text-lg my-0.5">{i === 6 ? "🧰" : i === 5 ? "🎁" : <CoinIcon size={20} className="inline" />}</p>
              <p className={`text-[10px] font-extrabold ${claimed ? "text-emerald-400" : "text-[#E6B84C]"}`}>
                {claimed ? <Check size={12} className="inline" /> : `+${amt}`}
              </p>
              {i === 6 && <p className="text-[7px] text-zinc-500 leading-tight">& Mystery Gift</p>}
            </motion.div>
          );
        })}
      </div>

      <div className="glass rounded-2xl p-4 mt-5" data-testid="watch-bonus-card">
        <div className="flex items-center justify-between">
          <p className="text-white text-xs font-bold">📺 Watch a stream for 10 min: <span className="text-[#E6B84C]">+25 bonus coins</span></p>
          {data.watch.claimed ? (
            <span className="text-emerald-400 text-[10px] font-bold">Claimed ✓</span>
          ) : data.watch.minutes >= data.watch.target ? (
            <button data-testid="claim-watch-button" onClick={claimWatch} className="gradient-primary text-white text-[10px] font-bold px-3 py-1.5 rounded-full">Claim +25</button>
          ) : null}
        </div>
        <div className="mt-2.5 h-2 rounded-full bg-white/10 overflow-hidden">
          <div className="h-full bg-gradient-to-r from-[#8A2BE2] to-[#E6B84C] transition-all" style={{ width: `${(data.watch.minutes / data.watch.target) * 100}%` }} />
        </div>
        <p className="text-[9px] text-zinc-500 mt-1 text-right">{data.watch.minutes}/{data.watch.target} min</p>
      </div>

      <button data-testid="claim-daily-button" onClick={claim} disabled={busy || data.claimed_today}
        className="mt-6 w-full bg-gradient-to-r from-[#FDE08B] via-[#FF9E4D] to-[#FF007F] text-white font-extrabold font-display py-4 rounded-full shadow-[0_0_24px_rgba(255,0,127,0.45)] active:scale-[0.98] transition-transform disabled:opacity-50 tracking-wide">
        {data.claimed_today ? "CLAIMED — COME BACK TOMORROW" : busy ? "CLAIMING…" : `CLAIM ${data.today_amount} COINS`}
      </button>
      <p className="text-zinc-600 text-[10px] text-center mt-2">Keep your streak alive — miss a day and it resets!</p>
    </div>
  );
}
